
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExternalLink, Copy } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface WatchBandQRPopupProps {
  isOpen: boolean;
  onClose: () => void;
  qrImageUrl: string;
  publicUrl: string;
  profileId: string;
}

export const WatchBandQRPopup = ({ 
  isOpen, 
  onClose, 
  qrImageUrl, 
  publicUrl, 
  profileId 
}: WatchBandQRPopupProps) => {
  const { toast } = useToast();

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast({
        title: "Success",
        description: "Public URL copied to clipboard",
      });
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast({
        title: "Error",
        description: "Failed to copy URL",
        variant: "destructive"
      });
    }
  };

  const handleOpenUrl = () => {
    window.open(publicUrl, '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Watch Band QR Code</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-block p-4 bg-white rounded-lg border shadow-sm">
              <img src={qrImageUrl} alt="Watch Band QR Code" className="mx-auto w-48 h-48" />
            </div>
          </div>
          
          <div className="space-y-2">
            <p className="text-sm font-medium">Medical Emergency QR Code</p>
            <p className="text-xs text-muted-foreground break-all">
              Public URL: {publicUrl}
            </p>
          </div>

          <div className="flex space-x-2">
            <Button onClick={handleOpenUrl} className="flex-1 flex items-center space-x-2">
              <ExternalLink className="h-4 w-4" />
              <span>View Public Profile</span>
            </Button>
            <Button variant="outline" onClick={handleCopyUrl} className="flex items-center space-x-2">
              <Copy className="h-4 w-4" />
              <span>Copy URL</span>
            </Button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              <strong>Watch Band Instructions:</strong> Print this QR code and attach it to your watch band or medical ID bracelet for emergency access to your medical information.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
